import React, { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { toRelativeUrl } from '@okta/okta-auth-js';
import useAuth from '../auth/useAuth';
import { logger } from '../utils/logger';
import { oauthService } from '../services/oauthService';
import { hasAnyRole } from '../auth/roles';

// Routes that require at least USER or ADMIN role to access
const ROLE_PROTECTED_ROUTES = ['/update', '/onboard'];

/**
 * When REACT_APP_E2E_BYPASS=true (set only in the Playwright webServer command),
 * this guard is a no-op: all routes are rendered immediately without Okta checks.
 * NEVER set this variable in production builds.
 */
const IS_E2E_BYPASS = process.env.REACT_APP_E2E_BYPASS === 'true';

const RequiredAuth: React.FC = () => {
  const { oktaAuth, authState } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip all auth/role logic when running under Playwright
    if (IS_E2E_BYPASS) { return; }

    logger.debug('RequiredAuth: Checking auth state', {
      hasAuthState: !!authState,
      isAuthenticated: authState?.isAuthenticated,
      currentPath: window.location.pathname
    });

    if (!authState) {
      logger.debug('RequiredAuth: No auth state yet, waiting...');
      return;
    }

    if (!authState.isAuthenticated) {
      logger.info('RequiredAuth: Not authenticated, redirecting to login');
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin
      );
      oktaAuth.setOriginalUri(originalUri);
      navigate('/login');
      return;
    }

    // Role-based route guarding: /update and /onboard require USER or ADMIN
    const isRoleProtected = ROLE_PROTECTED_ROUTES.some((route) =>
      location.pathname.startsWith(route)
    );
    if (isRoleProtected) {
      const roles = oauthService.getUserRoles();
      if (!hasAnyRole(roles)) {
        logger.warn('RequiredAuth: User lacks required role, redirecting to unauthorized', { roles, path: location.pathname });
        navigate('/unauthorized');
        return;
      }
    }

    logger.debug('RequiredAuth: User is authenticated and authorised, rendering protected content');
  }, [authState, oktaAuth, navigate, location.pathname]);

  // E2E bypass: skip the Okta spinner/redirect entirely
  if (IS_E2E_BYPASS) {
    return <Outlet />;
  }

  // Show loading while checking authentication
  if (!authState || !authState.isAuthenticated) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  return <Outlet />;
};

export default RequiredAuth;
